/**
 * Extract plain text from a PDF using pdf.js (S4-09).
 * Runs in the browser — does not send binary PDF to the analyze API.
 */

/** 10 MB — keep parsing responsive in-browser */
export const MAX_PDF_BYTES = 10 * 1024 * 1024;

let workerConfigured = false;

async function ensurePdfWorker(): Promise<typeof import('pdfjs-dist')> {
  const pdfjsLib = await import('pdfjs-dist');
  if (!workerConfigured) {
    pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;
    workerConfigured = true;
  }
  return pdfjsLib;
}

export function isPdfFileName(fileName: string): boolean {
  return fileName.toLowerCase().endsWith('.pdf');
}

export function validatePdfFile(file: File): string | null {
  if (!isPdfFileName(file.name)) {
    return 'File must be a PDF';
  }
  if (file.size > MAX_PDF_BYTES) {
    return `PDF must be under ${Math.round(MAX_PDF_BYTES / (1024 * 1024))} MB`;
  }
  if (file.size === 0) {
    return 'PDF file is empty';
  }
  return null;
}

export async function extractTextFromPdf(file: File): Promise<string> {
  const validationError = validatePdfFile(file);
  if (validationError) {
    throw new Error(validationError);
  }

  const pdfjsLib = await ensurePdfWorker();

  const data = new Uint8Array(await file.arrayBuffer());
  const pdf = await pdfjsLib.getDocument({ data, useSystemFonts: true }).promise;

  const parts: string[] = [];
  for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
    const page = await pdf.getPage(pageNum);
    const content = await page.getTextContent();
    const pageText = content.items
      .map((item) => ('str' in item && typeof item.str === 'string' ? item.str : ''))
      .join(' ')
      .replace(/\s+/g, ' ')
      .trim();
    if (pageText) {
      parts.push(pageText);
    }
  }

  const text = parts.join('\n').trim();
  if (!text) {
    throw new Error(
      'No extractable text in this PDF. It may be scanned/image-only. Export as CSV or paste the text manually.'
    );
  }

  return text;
}
